
import { Component, computed, Input, OnInit, signal, Signal, WritableSignal } from '@angular/core';
import { NbDialogRef, NbDialogService } from '@nebular/theme';
import { Observable, take } from 'rxjs';

@Component({
  selector: 'app-base-selectable-list',
  standalone: true,
  imports: [],
  templateUrl: './base-selectable-list.component.html',
  styleUrl: '../base-form/base-form.component.scss',
})
export abstract class BaseSelectableListComponent<T> implements OnInit {

  @Input() public title: string;
  @Input() public inputLinkedElementIds: number[] = [];

  public customNbCardClass: string = '';

  constructor(
    protected nbDialogRef: NbDialogRef<BaseSelectableListComponent<T>>,
    protected dialogService: NbDialogService,
  ) { }

  // All elements available, not displayed, only used as a base for linked and not linked elements
  protected allElements: WritableSignal<T[]> = signal([]);
  protected allElementsLoading: WritableSignal<boolean> = signal(true);

  // Linked elements are the elements that are already linked to the current object
  protected linkedElements: Signal<T[]> = computed(() => {
    return this.allElements().filter(element =>
      this.linkedElementIds().includes(element['id']),
    );
  });
  protected linkedElementIds: WritableSignal<number[]> = signal([]);

  // Not linked elements are the elements that are not linked to the current object, but can be selected to be linked
  protected notLinkedElements: Signal<T[]> = computed(() => {
    return this.allElements().filter(element =>
      !this.linkedElementIds().includes(element['id'])
      && this.showInNotLinkedElements(element),
    );
  });
  // Elements that are finally displayed in the not linked elements list, after filtering and searching
  protected notLinkedElementsAfterFilter: Signal<T[]> = computed(() => {
    return this.notLinkedElements().filter(element =>
      element['label']?.toLowerCase().includes(this.searchValue()?.toLowerCase() || ''),
    );
  });
  /* Abstract method to be implemented by the child class
   * to define the conditions to show an element in the not linked elements list
   */
  protected abstract showInNotLinkedElements(element: T): boolean;

  // Selected elements are the elements that are currently selected to be linked
  protected selectedIds: WritableSignal<number[]> = signal([]);

  // Search an element in the not linked elements
  protected searchValue: WritableSignal<string> = signal('');
  protected search(value): void {
    this.searchValue.set(value);
  }

  ngOnInit(): void {
    this.setUpData();
    this.initializeBackdropClick();
  }

  protected setUpData(): void {
    // Load all available elements
    this.getAllElements$().subscribe((elements) => {
      this.allElements.set(elements);
      this.allElementsLoading.set(false);
    });

    // Load elements that are already linked
    this.linkedElementIds.set(this.inputLinkedElementIds);
  }

  /* Abstract method to be implemented by the child class
   * to get all elements to be displayed in the list
   */
  protected abstract getAllElements$(): Observable<any>;

  // Select or deselect an element to be linked
  public onCheckboxChange(elementId: number, checked: boolean): void {
    if (checked) {
      this.selectedIds.update((ids) => [...new Set([...ids, elementId])]);
    } else {
      this.selectedIds.update((ids) => ids.filter((id) => id !== elementId));
    }
  }

  // Move selected elements to the linked elements list and clear the search value
  public linkElements(): void {
    this.linkedElementIds.update((ids) => [...new Set([...ids, ...this.selectedIds()])]);
    this.selectedIds.set([]);
    this.searchValue.set('');
  }

  // Remove linked elements from the linked elements list
  public removeLinkedElement(elementId: number): void {
    this.linkedElementIds.update((ids) => ids.filter((id) => id !== elementId));
  }

  // Close the dialog and return the linked element ids
  public close(): void {
    this.customNbCardClass = 'slide-out';
    // Wait for the animation to finish before closing the dialog;
    setTimeout(() => this.nbDialogRef.close(
      this.linkedElementIds() || [],
    ), 150);
  }

  private initializeBackdropClick(): void {
    // Replace the default backdrop click close event by a custom one
    this.nbDialogRef.onBackdropClick.pipe(take(1)).subscribe(() =>
      this.close(),
    );
  }

}
